import React, { useEffect, useMemo, useState, useCallback } from "react";
import ExpenseTable from "./expenses/ExpenseTable";
import DI from "../../utility/DI";
import { environment } from "../../../Env_data/environment";
import { Tab, Tabs, Typography, IconButton, Tooltip, Box } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { AppContext } from "../../../App";
import { useContext } from "react";
import HomeBoard from "./HomeBoard";
import { FetchContext } from "../SideBar";
import { categoryOptions } from "../../../Constant";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import BillImageModal from "./BillImageModal";
import ConfirmDialog from "../../utility/ConfirmDialog";
import PageHeader from "../../utility/PageHeader";

export const getCategoryLabel = (value) => {
  return (
    categoryOptions?.find((obj) => obj?.value === value)?.label ??
    value ??
    "Uncategorized"
  );
};

// Base columns without renderCell for description (will be added in component with modal handler)
const baseColumns = [
  { field: "id", headerName: "ID", width: 75, filterable: false },
  { field: "title", headerName: "Title", width: 200 },
  {
    field: "category",
    headerName: "Category",
    width: 200,
    valueGetter: (value) => getCategoryLabel(value),
  },
  { field: "brand", headerName: "Brand", width: 150 },
  { field: "weight", headerName: "Weight", width: 140 },
  { field: "quantity", headerName: "Quantity", width: 140 },
  { field: "price", headerName: "Price", width: 140, type: "number" },
  { field: "total", headerName: "Total", width: 150, type: "number" },
  { field: "expense_detail", headerName: "Description", width: 280 },
  { field: "paid_by", headerName: "Paid by", width: 250 },
  {
    field: "expense_time",
    headerName: "Date",
    width: 200,
    valueFormatter: (value) => {
      // Format DATETIME "2025-03-18 13:33:00" to "Tue 18-Mar-2025"
      if (!value) return "";
      try {
        const date = new Date(value);
        const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleDateString("en-US", { month: "short" });
        const year = date.getFullYear();
        return `${dayName} ${day}-${month}-${year}`;
      } catch {
        return value;
      }
    },
  },
];

function Home(props) {
  const [loading, setLoading] = React.useState(false);
  const { setState } = useContext(AppContext);
  const { refresh, setRefresh } = useContext(FetchContext);
  const navigate = useNavigate();
  const {
    di: { urls, session, success, error, GET },
  } = props;
  const [currentHome, setCurrentHome] = React.useState(
    session?.homes?.[0]?.id ?? null
  );

  // Bill image modal state
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  // Delete transaction confirm dialog state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, title: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleOpenBillModal = useCallback((expense) => {
    setSelectedExpense(expense);
    setBillModalOpen(true);
  }, []);

  const handleCloseBillModal = useCallback(() => {
    setBillModalOpen(false);
    setSelectedExpense(null);
  }, []);

  const handleDelete = useCallback(async (id) => {
    setDeleteLoading(true);
    await GET(urls.get.delete_home_expense, {
      transaction_id: id,
    })
      .then((res) => {
        if (res?.success) {
          success(res?.message ?? "Successfully deleted");
          setDeleteConfirm({ open: false, id: null, title: "" });
        } else {
          error(res?.message ?? "Error deleting transaction");
        }
      })
      .catch((er) => {
        error("Oops..! session out. Refresh the page");
      })
      .finally(() => {
        setDeleteLoading(false);
        setRefresh((prev) => !prev);
      });
  }, [urls.get.delete_home_expense, GET, success, error, setRefresh]);

  const columnsWithDelete = useMemo(() => {
    // Build a fresh copy of columns each time
    const cols = baseColumns.map((col) => {
      // Override the description column to include bill doc icon inline
      if (col.field === "expense_detail") {
        return {
          ...col,
          renderCell: (params) => {
            const billCount = parseInt(params.row.bill_count) || 0;
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                {billCount > 0 && (
                  <Tooltip
                    title={`${billCount} bill image${billCount > 1 ? "s" : ""} — click to view`}
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenBillModal(params.row);
                      }}
                      sx={{
                        color: "#1a237e",
                        backgroundColor: "rgba(26, 35, 126, 0.08)",
                        minWidth: 28,
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: "rgba(26, 35, 126, 0.18)",
                          transform: "scale(1.12)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "0.82rem",
                  }}
                >
                  {params.value}
                </Typography>
              </Box>
            );
          },
        };
      }
      return col;
    });

    // Add delete column at the beginning
    if (!cols.find((el) => el.field === "delete"))
      cols.splice(0, 0, {
        field: "delete",
        headerName: "Delete",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="delete"
            size="small"
            color="error"
            disabled={session.id !== params.row.created_by}
            onClick={() => {
              setDeleteConfirm({
                open: true,
                id: params.row.id,
                title: params.row.title || `Transaction #${params.row.id}`,
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      });

    return cols;
  }, [handleOpenBillModal, session.id]);
  const handleChange = (event, newValue) => {
    setCurrentHome(newValue);
    setRefresh((prev) => !prev);
  };
  const getHomes = async () => {
    if (!session?.id) {
      error("User ID cannot be null");
      return;
    }
    setLoading(true);
    await GET(urls.get.get_homes, {
      user_id: session?.id,
    })
      .then((res) => {
        if (res?.success) {
          // success(res?.message ?? "Homes fetched successfully");
          if (session !== undefined)
            setState((prev) => ({
              ...prev,
              session: { ...prev.session, homes: res.homes },
            }));
        } else {
          error(res?.message ?? "Error fetching homes");
        }
        setLoading(false);
      })
      .catch((er) => {
        error("Oops..! session out. Refresh the page");
        setLoading(false);
      });
  };

  useEffect(() => {
    getHomes();
  }, []);

  return (
    <div className="App">
      {/* ===== PAGE HEADING: Welcome to Home's Manager ===== */}
      <PageHeader
        title="Welcome to Home's Manager"
        subtitle="Manage household living expenses, groceries, and wallets"
        badgeText="Household Hub"
        icon={<HomeIcon />}
        gradient="linear-gradient(135deg, rgba(16, 185, 129, 0.22) 0%, rgba(20, 184, 166, 0.22) 100%)"
      />

      {currentHome === null ? (
        <Typography
          variant="p"
          component="p"
          sx={{ marginLeft: 1, marginX: 1 }}
          gutterBottom
          align="center"
        >
          {
            "You have not added any home yet. Please add a home to manage expenses."
          }
        </Typography>
      ) : null}
      <HomeBoard
        currentHome={currentHome}
        key={"dashboardHome"}
        refresh={refresh}
      />
      <br />
      {currentHome && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentHome}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable auto tabs example"
            sx={{ flex: 1 }}
          >
            {session?.homes?.map(({ id, name }) => (
              <Tab
                key={id}
                icon={<HomeIcon />}
                iconPosition="start"
                label={name.toUpperCase()}
                value={id}
              />
            ))}
          </Tabs>
          <IconButton
            id="home-settings-icon"
            onClick={() => navigate("/panel/manage?section=homes")}
            title="Manage Homes"
            sx={{ color: "text.secondary" }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
          </Box>
          <ExpenseTable
            key={currentHome}
            mode="server"
            apiEndpoint={environment.BASE_URL + urls.get.get_home_expenses}
            docTitle="Home expense" //for excel title
            columns={columnsWithDelete}
            onRowClick={(data) => console.log("row clicked", data)} //on row click
            isRowSelectable={(params) => {
              return true;
            }} //callbak for row selectable or not callback
            onCellClick={(data) => {
              console.log({ data });
            }}
            getRowClassName={(params) => {}}
            defaultSelection={(params) => {}}
            session={session}
            currentHome={currentHome}
            onOpenBillModal={handleOpenBillModal}
            onDeleteExpense={(row) => {
              setDeleteConfirm({
                open: true,
                id: row.id,
                title: row.title || `Transaction #${row.id}`,
              });
            }}
          />
        </>
      )}

      {/* Bill Image Viewer Modal */}
      <BillImageModal
        open={billModalOpen}
        onClose={handleCloseBillModal}
        expense={selectedExpense}
      />

      {/* Delete Expense Confirm Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, title: "" })}
        title="Confirm Delete Expense"
        content={`Are you sure you want to delete "${deleteConfirm.title}" (ID: #${deleteConfirm.id})? This will revert any auto-deductions from member wallets.`}
        confirmText="Delete"
        confirmColor="error"
        loading={deleteLoading}
        onConfirm={() => deleteConfirm.id && handleDelete(deleteConfirm.id)}
      />
    </div>
  );
}

export default DI(Home);
